import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import {Workflow} from '../../models/workflow.models';
import {Customer} from '../../../customer/model/customer';
import {Address} from '../../../customer/model/address';
import {Country} from '../../../customer/model/country';
import {MailModalService} from '../../../mail/mail-modal.service';
import {InvoiceSendModalService} from '../../invoice/send/invoice-send-modal.service';
import {PdfHistoryModalService} from '../../../history/pdf-history-modal.service';
import {DateHelper} from '../../../../helper/date-helper';
import {ignoreRejection} from '../../../../helper/ignore_rejection';
import {BigFetchWithCleanPosition} from './fetch-box.model';
import {PriceIncreaseFlex} from '../../models/price-increase-flex.model';
import {MergedFrame} from '../../models/frame.models';
import {FETCH_STATUS} from '../../../../react/fetch/fetch-status.model';
import {INVOICE_STATUS} from '../../invoice/invoice-status.models';

@Component({
    selector: 'fetch-box-inner',
    templateUrl: './fetch-box-inner.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FetchBoxInnerComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() workflow: Workflow;
  @Input() customer: Customer;
  @Input() addresses: Address[];
  @Input() fetch: BigFetchWithCleanPosition;
  @Input() index: number;
  @Input() frame: MergedFrame;
  @Input() isoCountry: Country;
  @Input() priceIncreaseFlex?: PriceIncreaseFlex;
  @Input() selected = false;
  @Output() statusChange = new EventEmitter<number>();
  @Output() reloadFetches: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('head', {static: true}) head: ElementRef<HTMLElement>;

  init = false;
  country: string;
  is_german: boolean;
  hasPriceIncrease: boolean;

  constructor(private mailModalService: MailModalService,
              private invoiceSendModalService: InvoiceSendModalService,
              private pdfHistoryModalService: PdfHistoryModalService) {
  }

  ngOnInit(): void {
    this.updateHasPriceIncrease();

    const innerAddresses: { [id: string]: Address } = {};
    this.addresses.forEach(data => innerAddresses[data.id] = data);
    const addressId = this.addressId();
    this.country = !addressId ? null : innerAddresses[addressId].country;
    this.is_german = this.country === 'DEU';
    this.init = true;
  }

  ngAfterViewInit(): void {
    if (this.selected) {
      this.head.nativeElement.scrollIntoView();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.priceIncreaseFlex && !changes.priceIncreaseFlex.isFirstChange()) {
      this.updateHasPriceIncrease();
    }
    if (changes.fetch && !changes.fetch.isFirstChange()) {
      this.updateHasPriceIncrease();
    }
  }

  // either de or eu with de vat
  get isEuWithDeVat(): boolean {
    if (this.fetch.contract_obj && this.fetch.contract_obj.ust) {
      return this.is_german || this.isoCountry.vat && this.fetch.contract_obj.ust.startsWith('DE');
    } else {
      return this.is_german;
    }
  }

  mail(): void {
    this.mailModalService.open(
      this.fetch.contract_obj.id,
      'contract',
      null,
      null,
      'de',
      this.workflow.id,
      true,
      this.frame.typ
    ).subscribe(() => {
      // if the contract_obj does not exist we actually do not set a date_sent
      if (this.fetch.contract_obj) {
        this.fetch.contract_obj.date_sent = DateHelper.format(new Date());
      }
    }, ignoreRejection);
  }

  invoiceSend(): void {
    const flipper = (state) => {
      this.fetch.invoice_obj.status = state;
      this.fetch.invoice_obj.date_sent = DateHelper.format(new Date());
    };
    this.invoiceSendModalService.open(this.fetch.invoice_obj.id, flipper)
      .subscribe(ignoreRejection, ignoreRejection);
  }

  history(name: string, objId: number, title: string): void {
    this.pdfHistoryModalService.open(name, objId, title).subscribe(ignoreRejection, ignoreRejection);
  }

  private addressId(): string {
    if (this.workflow.delivery_id) {
      return this.workflow.delivery_id;
    } else if (!this.workflow.delivery_id && this.workflow.invoice_id) {
      return this.workflow.invoice_id;
    } else {
      return this.customer.invoice.id;
    }
  }

  statusUpdated(newStatus: number): void {
    this.fetch.status = newStatus;
    this.statusChange.emit(this.workflow.id);
  }

  private updateHasPriceIncrease(): void {
    this.hasPriceIncrease = (
      (this.fetch.status < FETCH_STATUS.CLEAR) ?
        !!this.priceIncreaseFlex :
        !!this.fetch.positions.find(p => p.typ === 'article' && p.price_increase > 0)
    );
  }

  protected readonly FETCH_STATUS = FETCH_STATUS;
  protected readonly INVOICE_STATUS = INVOICE_STATUS;
}
